/**
 * WCAG-AA contrast utilities (audit finding B-19).
 *
 * Pure, dependency-free helpers implementing the WCAG 2.x relative-luminance
 * and contrast-ratio algorithms. Used to warn (or, behind a flag, block) when a
 * palette color or a custom hex entry would place near-invisible text/foreground
 * on its background — e.g. white-on-near-white, which the AI palette generator
 * and the custom-hex input both currently allow with no signal.
 *
 * Spec references:
 *   - Relative luminance: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 *   - Contrast ratio:     https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 *
 * AA thresholds:
 *   - Normal text:        >= 4.5 : 1
 *   - Large text / UI:    >= 3.0 : 1
 */

export const WCAG_AA_NORMAL = 4.5;
export const WCAG_AA_LARGE = 3.0;

/**
 * gordon-0918-themes-modal-side-pane-icons-accent-borders-corners-linkvault:
 * "make the auto light/dark [accent-ink] rule less strict — the accent
 * should show as the accent, only fall back when truly unreadable". A
 * decorative accent BORDER (profile card / wheel card / LinkVault) is not
 * body text and doesn't need full WCAG AA/UI compliance to read as intended
 * — only a floor well below WCAG_AA_LARGE so an accent that's merely a
 * little low-contrast still paints as itself, and only a genuinely
 * near-invisible pairing (creator set accent === background, or close to
 * it) still triggers the dark/light fallback.
 */
export const WCAG_MIN_VISIBLE = 1.5;

/**
 * Parse a hex color (#RGB, #RRGGBB, with or without leading #) into [r, g, b]
 * 0-255 integers. Returns null for anything unparseable (the callers treat a
 * null as "can't evaluate" and skip the guard rather than throwing).
 */
export function parseHexColor(hex) {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    // expand shorthand #abc -> #aabbcc
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const int = parseInt(h, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/**
 * Convert a single 8-bit channel (0-255) to its linearized value per WCAG.
 */
function linearizeChannel(channel8bit) {
  const c = channel8bit / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * WCAG relative luminance (0 = black, 1 = white) for a hex color.
 * Returns null if the color cannot be parsed.
 */
export function relativeLuminance(hex) {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(linearizeChannel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Contrast ratio between two hex colors, in the range [1, 21].
 * Order-independent. Returns null if either color cannot be parsed.
 */
export function contrastRatio(hexA, hexB) {
  const lumA = relativeLuminance(hexA);
  const lumB = relativeLuminance(hexB);
  if (lumA == null || lumB == null) return null;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Does the foreground/background pair meet WCAG AA?
 * @param {string} foreground hex
 * @param {string} background hex
 * @param {{ largeText?: boolean }} [opts] use the 3:1 large-text/UI threshold
 * @returns {boolean} true if it passes (also true when contrast can't be
 *          evaluated, so an unparseable color never blocks the user).
 */
export function meetsContrastAA(foreground, background, opts = {}) {
  const ratio = contrastRatio(foreground, background);
  if (ratio == null) return true; // un-evaluable -> don't block
  const threshold = opts.largeText ? WCAG_AA_LARGE : WCAG_AA_NORMAL;
  return ratio >= threshold;
}

/**
 * Build a human-readable warning for a low-contrast pair, or null if the pair
 * passes AA (or can't be evaluated). This is the function UI surfaces consume:
 * a non-null return = show a warning; null = stay quiet.
 *
 * @returns {{ ratio: number, required: number, message: string } | null}
 */
export function getContrastWarning(foreground, background, opts = {}) {
  const ratio = contrastRatio(foreground, background);
  if (ratio == null) return null;
  const required = opts.largeText ? WCAG_AA_LARGE : WCAG_AA_NORMAL;
  if (ratio >= required) return null;
  const rounded = Math.round(ratio * 100) / 100;
  return {
    ratio: rounded,
    required,
    message: `Low contrast (${rounded}:1, WCAG AA needs ${required}:1). This color may be hard to read against its background.`,
  };
}

/**
 * Brand ink (dark) + white — the two legible text colors D-11 chooses between
 * for text over a user-customizable background (`#123051` is the app ink token,
 * see utils/theme.js textBlue). Exposed so callers can pass alternates.
 */
export const INK_TEXT = "#123051";
export const WHITE_TEXT = "#FFFFFF";

/**
 * gordon-0921-WEB-gallery-theme-hover-and-selected-highlights-must-use-the-
 * theme-accent-colour-sweep: text painted directly ON a SOLID, arbitrary
 * (creator-chosen) accent fill is a harder case than INK_TEXT/WHITE_TEXT was
 * designed for -- the branded ink `#123051` is not dark enough to guarantee
 * AA against a midtone accent (e.g. `#9B5AF8`, `#E53935`): max(contrast(INK_TEXT,
 * accent), contrast(WHITE_TEXT, accent)) can fall below 4.5:1 for hues in
 * that midtone band (measured: both ~4.0-4.2:1 for the brand purple itself).
 * Pure black paired with pure white has no such gap: for ANY background,
 * max(contrastRatio("#000000", bg), contrastRatio("#FFFFFF", bg)) >= ~4.58:1
 * (the two contrast curves cross at background luminance ~0.179, where both
 * equal ~4.58) -- always >= WCAG_AA_NORMAL. Use this pair (not INK_TEXT) for
 * "text on an arbitrary solid fill" callers that must guarantee AA by
 * construction, never a hand-picked hex.
 */
export const BLACK_TEXT = "#000000";

// rgb()/rgba(...) -> "#RRGGBB", or null if it isn't an rgb() expression / is
// out of range. Matches the FIRST rgb() occurrence, so it also pulls the first
// color stop out of a gradient like `linear-gradient(rgb(20,20,20), ...)`.
function rgbExprToHex(str) {
  const m = String(str).match(
    /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
  );
  if (!m) return null;
  const parts = [m[1], m[2], m[3]].map(Number);
  if (parts.some((n) => n > 255)) return null;
  const h = (n) => n.toString(16).padStart(2, "0");
  return `#${h(parts[0])}${h(parts[1])}${h(parts[2])}`;
}

/**
 * Resolve an arbitrary CSS background value to a single representative hex color
 * for luminance sampling — the "effective background" D-11 needs:
 *   - solid hex (`#abc` / `#RRGGBB`, ± leading #) -> that hex
 *   - `rgb()` / `rgba()` -> hex
 *   - gradient / compound value -> its first parseable color stop (hex or rgb).
 *     This is a cheap approximation of D-11's "sample dominant/average
 *     luminance" open item — good enough to pick text color from the leading
 *     stop; a true multi-stop average is a later refinement.
 *   - `url(...)` image / unparseable -> null (cannot sample statically).
 * Returns a hex string or null.
 */
export function effectiveBgHex(background) {
  if (typeof background !== "string") return null;
  const s = background.trim();
  if (!s || /^url\(/i.test(s)) return null;
  if (parseHexColor(s)) return s.startsWith("#") ? s : `#${s}`;
  const asRgb = rgbExprToHex(s);
  if (asRgb) return asRgb;
  // gradient / compound: take the first hex color stop if present.
  const firstHex = s.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/);
  if (firstHex && parseHexColor(firstHex[0])) return firstHex[0];
  return null;
}

/**
 * D-11: auto-derive a legible text color for text sitting on a customizable
 * background. Returns whichever of the ink (`#123051`) / white pair has the
 * higher WCAG contrast against the effective background, so a dark-purple
 * button never renders black (illegible) text — no manual step. When the
 * background cannot be resolved (image url, unparseable) it returns
 * `opts.fallback` (default null) so the caller keeps its own default and the
 * pick never throws.
 *
 * @param {string} background any CSS background value (hex, rgb(), gradient…)
 * @param {{ dark?: string, light?: string, fallback?: string|null }} [opts]
 * @returns {string|null}
 */
export function autoTextColor(background, opts = {}) {
  const dark = opts.dark || INK_TEXT;
  const light = opts.light || WHITE_TEXT;
  const fallback = "fallback" in opts ? opts.fallback : null;
  const hex = effectiveBgHex(background);
  if (!hex) return fallback;
  const darkRatio = contrastRatio(dark, hex);
  const lightRatio = contrastRatio(light, hex);
  if (darkRatio == null || lightRatio == null) return fallback;
  return lightRatio > darkRatio ? light : dark;
}

/**
 * gordon-0913-gallery-theme-auto-contrast-and-follow-icons: extends D-11's
 * autoTextColor for the "branded accent" case (Gallery Theme's LinkVault
 * border/text, Follow-icon tint) instead of a new formula. Unlike
 * autoTextColor (which always picks whichever of dark/light wins), a themed
 * accent should PAINT AS ITSELF whenever it already reads fine against its
 * background — that's the whole point of a creator picking an accent color.
 * Only when the accent fails AA against that background (most commonly: the
 * creator set accent === background, or something close to it) does this
 * fall back to autoTextColor's dark/light ink pick.
 *
 * @param {string} accent hex accent color normally painted as border/text/tint
 * @param {string} backgroundHex the effective background it sits on
 * @param {{ largeText?: boolean, minVisible?: boolean, dark?: string, light?: string }} [opts]
 *        largeText picks the 3:1 (icons/borders) vs 4.5:1 (text) threshold;
 *        minVisible (Gordon 2026-09-18) overrides both with the much lower
 *        WCAG_MIN_VISIBLE floor for purely decorative accent borders.
 * @returns {string} accent unchanged if it already passes AA, else an
 *          auto-picked dark/light ink; the unresolvable-color fallback is
 *          `accent` itself (never blocks rendering).
 */
export function autoAccentInk(accent, backgroundHex, opts = {}) {
  const ratio = contrastRatio(accent, backgroundHex);
  const required = opts.minVisible
    ? WCAG_MIN_VISIBLE
    : opts.largeText
    ? WCAG_AA_LARGE
    : WCAG_AA_NORMAL;
  if (ratio == null || ratio >= required) return accent;
  return autoTextColor(backgroundHex, { ...opts, fallback: accent });
}

// Convert an [r,g,b] 0-255 triple to [h, s, l] (h in [0,360), s/l in [0,1]).
function rgbToHsl([r8, g8, b8]) {
  const r = r8 / 255;
  const g = g8 / 255;
  const b = b8 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    default:
      h = (r - g) / d + 4;
  }
  return [h * 60, s, l];
}

// [h(0-360), s(0-1), l(0-1)] -> "#rrggbb".
function hslToHexColor(h, s, l) {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.max(0, Math.min(1, s));
  const light = Math.max(0, Math.min(1, l));
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;
  let seg;
  if (hue < 60) seg = [c, x, 0];
  else if (hue < 120) seg = [x, c, 0];
  else if (hue < 180) seg = [0, c, x];
  else if (hue < 240) seg = [0, x, c];
  else if (hue < 300) seg = [x, 0, c];
  else seg = [c, 0, x];
  const toByte = (v) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toByte(seg[0])}${toByte(seg[1])}${toByte(seg[2])}`;
}

// gordon-0918c-accent-on-small-surfaces-legible-family-color-toast: "find the
// CLOSEST legible color in the accent family to the selected one ... i.e.
// gray instead of white, or pink instead of red". Unlike autoAccentInk (which
// falls back to an unrelated dark/light INK color, a different family
// entirely), this stays in the accent's OWN hue: walk HSL lightness in fixed
// steps -- toward dark on a light surface, toward light on a dark one -- and
// return the first step that clears `minRatio`. Hue and saturation are never
// touched, so the result always reads as a member of the same color family as
// the input, never a hue jump.
//
// @param {string} accent hex accent color
// @param {string} surfaceBg hex background it sits on
// @param {number} [minRatio] WCAG contrast floor to clear (default 3:1, the
//        WCAG non-text/UI-component threshold -- WCAG_AA_LARGE)
// @returns {string} accent unchanged when it already clears minRatio; else
//          the nearest same-hue lightness step that does. The unresolvable-
//          color fallback is `accent` itself (never blocks rendering).
export function legibleAccent(accent, surfaceBg, minRatio = WCAG_AA_LARGE) {
  const ratio = contrastRatio(accent, surfaceBg);
  if (ratio == null || ratio >= minRatio) return accent;
  const rgb = parseHexColor(accent);
  const bgLuminance = relativeLuminance(surfaceBg);
  if (!rgb || bgLuminance == null) return accent;
  const [h, s, l] = rgbToHsl(rgb);
  const darken = bgLuminance >= 0.5;
  const STEP = 0.02;
  const MAX_STEPS = 50;
  for (let i = 1; i <= MAX_STEPS; i += 1) {
    const nextL = darken ? l - i * STEP : l + i * STEP;
    if (nextL < 0 || nextL > 1) break;
    const candidate = hslToHexColor(h, s, nextL);
    if (contrastRatio(candidate, surfaceBg) >= minRatio) return candidate;
  }
  // Extreme end of the same-hue walk (0% or 100% lightness) -- guaranteed the
  // highest-contrast member of this hue family against any background.
  return hslToHexColor(h, s, darken ? 0 : 1);
}

/**
 * hex + alpha -> "rgba(r, g, b, a)", a translucent CSS fill for a "very pale
 * tint of the accent" (gordon-0921 owner rule: themed-gallery hover/selected
 * FILLS take a ~10-14% alpha tint of the accent, never a hand-picked hex).
 * Returns null when the hex can't be parsed (caller keeps its own fallback).
 */
export function hexToRgba(hex, alpha) {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * Approximate a wallpaper's visible color AFTER its CSS hue-overlay tint
 * (see CardWallpaperBackground's `&::after`: a solid color, `mixBlendMode:
 * "multiply"`, at `opacity: huePercentage/100`) is painted over it, so
 * luminance can be sampled post-tint rather than on the untinted base — a
 * strong dark tint over a light base should read as dark, and vice versa.
 * Pure per-channel multiply blend, cross-faded by the overlay opacity;
 * mirrors the CSS compositing exactly for two flat hex colors.
 *
 * @param {string} baseHex the untinted background hex
 * @param {string} tintHex the overlay hue color hex
 * @param {number} opacity 0-1 overlay opacity
 * @returns {string} resulting hex, or `baseHex` unchanged if either color is
 *          unparseable or opacity is falsy (nothing to blend).
 */
export function applyMultiplyTint(baseHex, tintHex, opacity) {
  const base = parseHexColor(baseHex);
  const tint = parseHexColor(tintHex);
  if (!base || !tint || !opacity) return baseHex;
  const clampedOpacity = Math.max(0, Math.min(1, opacity));
  const h = (n) => n.toString(16).padStart(2, "0");
  const blended = base
    .map((b, i) => (b * tint[i]) / 255)
    .map((multiplied, i) => Math.round(base[i] * (1 - clampedOpacity) + multiplied * clampedOpacity));
  return `#${blended.map(h).join("")}`;
}
